
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { UserAvatar } from '@/components/UserAvatar';
import { UserProgressSection } from '@/components/UserProgressSection';
import { toast } from 'sonner';
import { Camera, Save, X, Loader2, Banknote, CheckCircle2, AlertCircle, ExternalLink, Bell, Moon, Globe, Lock, AlertTriangle, Shield, LogOut } from 'lucide-react';
import { PortfolioGrid } from '@/components/portfolio/PortfolioGrid';
import { AddPortfolioItem } from '@/components/portfolio/AddPortfolioItem';
import { usePortfolio } from '@/hooks/usePortfolio';
import { useProfileUpdates } from '@/hooks/useProfileUpdates';
import { useDarkMode } from '@/contexts/DarkModeContext';
import { useUserRole } from '@/hooks/useUserRole';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import ConnectOnboarding from './ConnectOnboarding';
import { MyReviewsSection } from '@/components/MyReviewsSection';
import { validatePassword } from '@/utils/passwordValidation';
import { authRateLimiter } from '@/utils/inputValidation';

interface ProfileData {
  first_name: string;
  last_name: string;
  bio: string;
  avatar_url: string;
  school: string;
  phone_number: string;
  stripe_account_id?: string | null;
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
  date_of_birth?: string | null;
}

const MyProfile = () => {
  const { user, signOut, loading: authLoading } = useAuth();
  const { isAdmin } = useUserRole();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { isEnabled: pushEnabled, toggleNotifications } = usePushNotifications();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [stripeError, setStripeError] = useState('');
  
  // Settings state
  const [emailUpdates, setEmailUpdates] = useState(true);
  const [timezone, setTimezone] = useState('');
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [sendingResetCode, setSendingResetCode] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [useEmailReset, setUseEmailReset] = useState(false);

  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  const [profileData, setProfileData] = useState<ProfileData>({
    first_name: '',
    last_name: '',
    bio: '',
    avatar_url: '',
    school: '',
    phone_number: ''
  });

  const [originalData, setOriginalData] = useState<ProfileData>({
    first_name: '',
    last_name: '',
    bio: '',
    avatar_url: '',
    school: '',
    phone_number: ''
  });

  const { portfolioItems, loading: portfolioLoading, refetch: refetchPortfolio, deletePortfolioItem } = usePortfolio(user?.id);
  const { profileVersion } = useProfileUpdates();

  const [connectLoading, setConnectLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState<{ connected: boolean; charges_enabled: boolean; payouts_enabled: boolean; details_submitted?: boolean; account_id?: string }>({
    connected: false,
    charges_enabled: false,
    payouts_enabled: false,
  });

  const [balance, setBalance] = useState<{ available: number; pending: number } | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [isMinorOnboardingOpen, setIsMinorOnboardingOpen] = useState(false);

  // Initialize timezone on mount
  useEffect(() => {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(detectedTimezone);
  }, []);

  // If the user is signed out (e.g., after password reset), don’t leave them on My Profile
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/auth?mode=login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Password-recovery links should go to the dedicated reset page (not My Profile)
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');

    if (type === 'recovery' && accessToken) {
      window.location.replace(`/reset-password${window.location.hash}`);
    }
  }, []);

  const handleTimezoneChange = () => {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(detectedTimezone);
    toast.success('Timezone refreshed');
  };

  const handleVerifyCurrentPassword = async () => {
    if (!currentPassword.trim()) {
      setPasswordError('Current password is required');
      return;
    }
    
    setVerifyingPassword(true);
    setPasswordError('');
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword.trim()
      });
      
      if (error) {
        throw new Error('Current password is incorrect');
      }
      
      setPasswordVerified(true);
      toast.success('Password verified! Now enter your new password.');
    } catch (error: any) {
      setPasswordError(error.message || 'Failed to verify password');
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (!passwordVerified) {
      setPasswordError('Please verify your identity first');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setPasswordError(validation.errorMessage);
      return;
    }

    setChangingPassword(true);

    try {
      // If using email reset flow, update password via edge function
      if (useEmailReset) {
        if (!resetCode) {
          throw new Error("Missing verification code. Please go back and re-enter it.");
        }

        const { data, error } = await supabase.functions.invoke("verify-password-reset-code", {
          body: { email: user?.email, code: resetCode, newPassword },
        });

        if (error) throw new Error(error.message || "Failed to update password");
        if (data?.error) throw new Error(data.error);
        if (!data?.success || !data?.passwordUpdated) {
          throw new Error("Failed to update password. Please try again.");
        }

        toast.success("Password updated successfully");

        // Reset UI state
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordVerified(false);
        setResetCode("");
        setResetCodeSent(false);
        setUseEmailReset(false);
        setPasswordDialogOpen(false);
        return;
      }

      // Otherwise use the standard Supabase auth update (for current password verification flow)
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordVerified(false);
      setResetCode('');
      setResetCodeSent(false);
      setUseEmailReset(false);
      setPasswordDialogOpen(false);
    } catch (error: any) {
      console.error('Error updating password:', error);
      setPasswordError(error.message || 'Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setDeleteError('');

    if (!deletePassword.trim()) {
      setDeleteError('Password is required');
      return;
    }

    const userKey = user?.email || user?.id || 'anonymous';
    if (!authRateLimiter.isAllowed(`delete_account_${userKey}`)) {
      setDeleteError('Too many attempts. Please wait before trying again.');
      return;
    }

    setDeleting(true);

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: deletePassword.trim()
      });

      if (authError) {
        throw new Error('Incorrect password. Please try again.');
      }

      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: {
          targetUserId: user?.id,
          userEmail: user?.email,
          selfDelete: true
        }
      });

      if (error) throw new Error(error.message || 'Failed to delete account');
      if (data?.error) throw new Error(data.error);

      await signOut();
      authRateLimiter.reset(`delete_account_${userKey}`);
      toast.success('Your account has been deleted.');
      navigate('/');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setDeleteError(error.message || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const fetchProfile = async () => {
    if (!user) {
      setProfileError('You must be logged in to view this page');
      setLoading(false);
      return;
    }

    setLoading(true);
    setProfileError('');

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        setProfileError('Could not load profile data.');
      } else {
        const profile: ProfileData = {
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          bio: data.bio || '',
          avatar_url: data.avatar_url || '',
          school: data.school || '',
          phone_number: data.phone_number || '',
          date_of_birth: data.date_of_birth || null,
          stripe_account_id: data.stripe_account_id,
          charges_enabled: data.charges_enabled,
          payouts_enabled: data.payouts_enabled,
        };
        setProfileData(profile);
        setOriginalData(profile);
        
        const hasAccount = !!data.stripe_account_id;
        
        setConnectStatus({
          connected: hasAccount,
          charges_enabled: !!data.charges_enabled,
          payouts_enabled: !!data.payouts_enabled,
          details_submitted: data.charges_enabled || data.payouts_enabled,
          account_id: data.stripe_account_id || undefined,
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setProfileError('An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const refreshConnectStatus = useCallback(async () => {
    if (!user) return;
    setStripeError('');
    try {
      const { data, error } = await supabase.functions.invoke('connect-account-status');
      if (error) throw error;
      
      if (data) {
        setConnectStatus({
          connected: !!data.connected,
          charges_enabled: !!data.charges_enabled,
          payouts_enabled: !!data.payouts_enabled,
          details_submitted: !!data.details_submitted,
          account_id: data.account_id,
        });
        
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();
          
          if (profileData && !profileError) {
            setProfileData(prev => ({
              ...prev,
              stripe_account_id: profileData.stripe_account_id,
              charges_enabled: profileData.charges_enabled,
              payouts_enabled: profileData.payouts_enabled,
            }));
          }
        } catch (e) {
          console.error('[MyProfile] Silent profile update error:', e);
        }
      }
    } catch (e: any) {
      console.error('[MyProfile] refreshConnectStatus error:', e);
      setStripeError('Unable to check payment status. Please try again.');
    }
  }, [user]);

  const fetchBalance = useCallback(async () => {
    if (!user) return;
    setBalanceLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-stripe-balance');
      if (error) throw error;
      
      if (data && data.connected) {
        setBalance({
          available: data.balance / 100,
          pending: data.pending / 100
        });
      } else {
        setBalance(null);
      }
    } catch (e: any) {
      console.error('[MyProfile] fetchBalance error:', e);
    } finally {
      setBalanceLoading(false);
    }
  }, [user]);

  const handleViewDashboard = useCallback(async () => {
    if (!user) return;
    setDashboardLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-stripe-dashboard-link');
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No dashboard URL returned');
      }
    } catch (e: any) {
      console.error('[MyProfile] handleViewDashboard error:', e);
      toast.error('Unable to open Stripe dashboard. Please try again.');
    } finally {
      setDashboardLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [user, profileVersion]);

  useEffect(() => {
    if (connectStatus.connected && connectStatus.payouts_enabled) {
      fetchBalance();
    }
  }, [connectStatus.connected, connectStatus.payouts_enabled, fetchBalance]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'stripe-onboarding-finished') {
        setIsMinorOnboardingOpen(false);
        refreshConnectStatus();
        toast.success('Stripe account connected!');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [refreshConnectStatus]);

  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'return') {
      toast.info('Checking your Stripe account status...');
      refreshConnectStatus().then(() => {
        navigate('/my-profile', { replace: true });
      });
    } else if (stripeParam === 'refresh') {
      toast.info('Your session expired. Please try again.');
      navigate('/my-profile', { replace: true });
    }
  }, [searchParams, refreshConnectStatus, navigate]);

  useEffect(() => {
    if (location.state?.startEditing) {
      setEditing(true);
    }
  }, [location.state]);

  const calcAge = (isoDate?: string | null) => {
    if (!isoDate) return null;
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  };

  const handleSetupPayouts = async () => {
    if (!user || !profileData) return;

    const age = calcAge(profileData.date_of_birth);
    
    if (age === null) {
      toast.error('Please set your date of birth in your profile first');
      return;
    }

    if (age >= 18) {
      await startOnboarding();
    } else {
      setIsMinorOnboardingOpen(true);
    }
  };

  const startOnboarding = async () => {
    setConnectLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('connect-create-account');

      if (error) {
        toast.error('Failed to create onboarding link: ' + (error.message || 'Unknown error'));
        return;
      }

      if (data?.alreadyEnabled) {
        toast.success('Test account connected!');
        await refreshConnectStatus();
        return;
      }

      const url = data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Failed to get onboarding link');
      }
    } catch (e: any) {
      toast.error('Unable to start payouts setup: ' + (e.message || 'Unknown error'));
    } finally {
      setConnectLoading(false);
    }
  };

  const resumeOnboarding = async () => {
    setConnectLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('connect-account-link');

      if (error) {
        toast.error('Failed to get resume link: ' + (error.message || 'Unknown error'));
        return;
      }

      const url = data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Failed to get resume link');
      }
    } catch (e: any) {
      toast.error('Unable to resume payouts setup: ' + (e.message || 'Unknown error'));
    } finally {
      setConnectLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    const fileType = file.type;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!validTypes.includes(fileType)) {
      toast.error('Please select a valid image file (JPG, PNG, GIF, or WEBP)');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image size should be less than 10MB');
      return;
    }

    setUploading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const avatarUrl = data.publicUrl;

      setProfileData(prev => ({
        ...prev,
        avatar_url: avatarUrl
      }));

      toast.success('Avatar uploaded successfully');
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    setProfileError('');

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          bio: profileData.bio,
          avatar_url: profileData.avatar_url,
          school: profileData.school || null,
          phone_number: profileData.phone_number || null,
          date_of_birth: profileData.date_of_birth ?? null,
        })
        .eq('id', user.id);

      if (error) throw error;

      setOriginalData({ ...profileData });
      setEditing(false);
      toast.success('Profile updated successfully');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile');
      setProfileError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setProfileData({ ...originalData });
    setEditing(false);
    setProfileError('');
  };

  const getInitials = () => {
    if (profileData.first_name && profileData.last_name) {
      return `${profileData.first_name[0]}${profileData.last_name[0]}`.toUpperCase();
    } else if (profileData.first_name) {
      return profileData.first_name[0].toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || '?';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="container mx-auto px-4">
          <div className="text-center py-8">
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="mb-8">
          <h1 className="font-poppins font-bold text-4xl mb-4">My Profile</h1>
          <p className="font-inter text-xl text-muted-foreground">
            Manage your profile, settings, and portfolio
          </p>
          {isAdmin && (
            <Badge variant="destructive" className="mt-2">
              <Shield className="w-3 h-3 mr-1" />
              Admin Account
            </Badge>
          )}
        </div>

        {/* Profile Information */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="font-poppins text-2xl">Profile Information</CardTitle>
              {!editing ? (
                <Button 
                  onClick={() => setEditing(true)}
                  variant="outline"
                  className="font-inter"
                >
                  Edit Profile
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    onClick={handleCancel}
                    variant="outline"
                    className="font-inter flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSave}
                    disabled={saving}
                    className="font-inter flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
              {profileError && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-destructive text-sm">{profileError}</p>
              </div>
            )}

            {/* Avatar Section */}
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <UserAvatar
                  userId={user?.id}
                  avatarUrl={profileData.avatar_url}
                  fallback={getInitials()}
                  size="xl"
                  showLevel={true}
                />
                
                {editing && (
                  <div className="absolute bottom-0 right-0">
                    <input
                      type="file"
                      id="avatar-upload"
                      className="hidden"
                      accept="image/jpeg, image/png, image/gif, image/webp"
                      onChange={handleAvatarUpload}
                      disabled={uploading}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full h-8 w-8 p-0"
                      onClick={() => document.getElementById('avatar-upload')?.click()}
                      disabled={uploading}
                    >
                      <Camera className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              {editing && (
                <p className="text-xs text-muted-foreground text-center">
                  {uploading ? 'Uploading...' : 'Click the camera icon to upload a new photo'}
                </p>
              )}
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                {editing ? (
                  <Input
                    id="first_name"
                    name="first_name"
                    value={profileData.first_name}
                    onChange={handleInputChange}
                    placeholder="Enter your first name"
                  />
                ) : (
                   <p className="py-2 px-3 bg-muted rounded-md min-h-[40px] flex items-center">
                     {profileData.first_name || 'Not provided'}
                   </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                {editing ? (
                  <Input
                    id="last_name"
                    name="last_name"
                    value={profileData.last_name}
                    onChange={handleInputChange}
                    placeholder="Enter your last name"
                  />
                ) : (
                  <p className="py-2 px-3 bg-muted rounded-md min-h-[40px] flex items-center">
                    {profileData.last_name || 'Not provided'}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                {editing ? (
                  <Input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    value={profileData.date_of_birth || ''}
                    onChange={handleInputChange}
                  />
                ) : (
                  <p className="py-2 px-3 bg-muted rounded-md min-h-[40px] flex items-center">
                    {profileData.date_of_birth || 'Not provided'}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Age</Label>
                <p className="py-2 px-3 bg-muted rounded-md min-h-[40px] flex items-center">
                  {calcAge(profileData.date_of_birth) ?? '—'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="school">School</Label>
                {editing ? (
                  <Input
                    id="school"
                    name="school"
                    value={profileData.school}
                    onChange={handleInputChange}
                    placeholder="e.g., Stanford University (optional)"
                  />
                ) : (
                  <p className="py-2 px-3 bg-muted rounded-md min-h-[40px] flex items-center">
                    {profileData.school || <span className="text-muted-foreground italic">Not provided</span>}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">Connect with other students</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number</Label>
                {editing ? (
                  <Input
                    id="phone_number"
                    name="phone_number"
                    type="tel"
                    value={profileData.phone_number}
                    onChange={handleInputChange}
                    placeholder="e.g., +1 (555) 123-4567"
                  />
                ) : (
                  <p className="py-2 px-3 bg-muted rounded-md min-h-[40px] flex items-center">
                    {profileData.phone_number || <span className="text-muted-foreground italic">Not provided</span>}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">For payment updates</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              {editing ? (
                <Textarea
                  id="bio"
                  name="bio"
                  value={profileData.bio}
                  onChange={handleInputChange}
                  placeholder="Tell us about yourself..."
                  rows={4}
                />
              ) : (
                <div className="py-2 px-3 bg-muted rounded-md min-h-[100px]">
                  {profileData.bio ? (
                    <p className="whitespace-pre-wrap">{profileData.bio}</p>
                  ) : (
                    <p className="text-muted-foreground italic">No bio provided</p>
                  )}
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="pt-4 border-t border-border">
              <div className="space-y-2">
                <Label>Email</Label>
                <p className="py-2 px-3 bg-muted rounded-md min-h-[40px] flex items-center text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Settings */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="font-poppins text-2xl flex items-center gap-2">
              <Lock className="h-5 w-5" /> Account Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Change Password</p>
                <p className="text-sm text-muted-foreground">Update your account password</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setPasswordDialogOpen(true)}>
                <Lock className="w-4 h-4 mr-2" />
                Change
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notifications & Appearance */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="font-poppins text-2xl flex items-center gap-2">
              <Bell className="h-5 w-5" /> Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Push Notifications</p>
                <p className="text-sm text-muted-foreground">Receive notifications in your browser</p>
              </div>
              <Switch 
                checked={pushEnabled} 
                onCheckedChange={toggleNotifications}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Email Updates</p>
                <p className="text-sm text-muted-foreground">Get notified about important updates via email</p>
              </div>
              <Switch 
                checked={emailUpdates} 
                onCheckedChange={(checked) => {
                  setEmailUpdates(checked);
                  toast.success(checked ? 'Email updates enabled' : 'Email updates disabled');
                }}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <Moon className="h-4 w-4" /> Dark Mode
                </p>
                <p className="text-sm text-muted-foreground">Switch between light and dark themes</p>
              </div>
              <Switch 
                checked={isDarkMode} 
                onCheckedChange={toggleDarkMode}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <Globe className="h-4 w-4" /> Time Zone
                </p>
                <p className="text-sm text-muted-foreground">
                  {timezone || 'Detecting...'}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleTimezoneChange}>
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>
        

        {/* Reviews Section */}
        <MyReviewsSection />

        {/* Portfolio Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="font-poppins text-2xl">Portfolio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <AddPortfolioItem onAdd={refetchPortfolio} />
              
              {portfolioLoading ? (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">Loading portfolio...</p>
                </div>
              ) : (
                <PortfolioGrid
                  items={portfolioItems}
                  isOwner={true}
                  onDelete={deletePortfolioItem}
                  onUpdate={refetchPortfolio}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Progress Section */}
        {user?.id && (
          <UserProgressSection userId={user.id} />
        )}

        {/* Sign Out */}
        <Card className="mb-4">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <LogOut className="h-4 w-4" /> Sign Out
                </p>
                <p className="text-sm text-muted-foreground">Sign out of your account on this device</p>
              </div>
              <Button variant="outline" size="sm" onClick={signOut}>
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Delete Account */}
        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Delete Account
                </p>
                <p className="text-sm text-muted-foreground">Permanently delete your account and all data</p>
              </div>
              <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
                Delete Account
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Minor Onboarding Modal */}
        <Dialog open={isMinorOnboardingOpen} onOpenChange={setIsMinorOnboardingOpen}>
          <DialogContent className="max-w-2xl p-0">
            <DialogHeader className="sr-only">
              <DialogTitle>Payouts setup</DialogTitle>
            </DialogHeader>
            <ConnectOnboarding embed isPopup />
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={(open) => {
          setPasswordDialogOpen(open);
          if (!open) {
            setResetCodeSent(false);
            setResetCode('');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setPasswordError('');
            setPasswordVerified(false);
            setUseEmailReset(false);
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
              <DialogDescription>
                {passwordVerified 
                  ? 'Enter your new password below.' 
                  : resetCodeSent
                    ? 'Enter the 6-digit code sent to your email.'
                    : 'Verify your identity to change your password.'}
              </DialogDescription>
            </DialogHeader>
            
            {!passwordVerified ? (
              <>
                {!resetCodeSent ? (
                  <>
                    {/* Option 1: Reset via Email Code */}
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Bell className="h-4 w-4 text-muted-foreground" />
                        <p className="font-medium text-sm">Forgot your password?</p>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        We'll send a 6-digit code to {user?.email}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          if (!user?.email) {
                            toast.error('No email associated with your account');
                            return;
                          }
                          setSendingResetCode(true);
                          setPasswordError('');
                          try {
                            const { data, error } = await supabase.functions.invoke('send-password-reset-code', {
                              body: { email: user.email }
                            });
                            if (error) throw error;
                            if (data?.error) throw new Error(data.error);
                            setResetCodeSent(true);
                            setUseEmailReset(true);
                            toast.success('Reset code sent to your email');
                          } catch (error: any) {
                            toast.error(error.message || 'Failed to send reset code');
                          } finally {
                            setSendingResetCode(false);
                          }
                        }}
                        disabled={sendingResetCode}
                      >
                        {sendingResetCode ? 'Sending...' : 'Send Reset Code'}
                      </Button>
                    </div>

                    {/* Divider */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">Or enter current password</span>
                      </div>
                    </div>

                    {/* Option 2: Verify with Current Password */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="currentPassword">Current Password</Label>
                        <Input
                          id="currentPassword"
                          type="password"
                          value={currentPassword}
                          onChange={(e) => { setCurrentPassword(e.target.value); setPasswordError(''); }}
                          placeholder="Enter current password"
                        />
                      </div>
                      {passwordError && (
                        <p className="text-sm text-destructive">{passwordError}</p>
                      )}
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button 
                          type="button" 
                          onClick={handleVerifyCurrentPassword}
                          disabled={verifyingPassword || !currentPassword.trim()}
                        >
                          {verifyingPassword ? 'Verifying...' : 'Verify'}
                        </Button>
                      </DialogFooter>
                    </div>
                  </>
                ) : (
                  /* Code Entry Step */
                  <div className="space-y-4">
                    <div className="p-3 border rounded-lg bg-muted/30">
                      <p className="text-sm text-muted-foreground">
                        We sent a 6-digit code to <span className="font-medium">{user?.email}</span>
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="resetCode">Enter Code</Label>
                      <Input
                        id="resetCode"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={resetCode}
                        onChange={(e) => { 
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setResetCode(value); 
                          setPasswordError(''); 
                        }}
                        placeholder="Enter 6-digit code"
                        className="text-center text-2xl tracking-widest font-mono"
                        autoFocus
                      />
                    </div>
                    {passwordError && (
                      <p className="text-sm text-destructive">{passwordError}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Didn't receive the code?{' '}
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={async () => {
                          if (!user?.email) return;
                          setSendingResetCode(true);
                          try {
                            const { data, error } = await supabase.functions.invoke('send-password-reset-code', {
                              body: { email: user.email }
                            });
                            if (error) throw error;
                            if (data?.error) throw new Error(data.error);
                            toast.success('New code sent');
                          } catch (error: any) {
                            toast.error(error.message || 'Failed to resend code');
                          } finally {
                            setSendingResetCode(false);
                          }
                        }}
                        disabled={sendingResetCode}
                      >
                        {sendingResetCode ? 'Sending...' : 'Resend code'}
                      </button>
                    </p>
                    <DialogFooter>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setResetCodeSent(false);
                          setResetCode('');
                          setPasswordError('');
                        }}
                      >
                        Back
                      </Button>
                      <Button 
                        type="button" 
                        onClick={async () => {
                          if (resetCode.length !== 6) {
                            setPasswordError('Please enter the 6-digit code');
                            return;
                          }
                          setVerifyingCode(true);
                          setPasswordError('');
                          try {
                            const { data, error } = await supabase.functions.invoke('verify-password-reset-code', {
                              body: { email: user?.email, code: resetCode }
                            });
                            
                            if (error) {
                              throw new Error(error.message || 'Failed to verify code');
                            }
                            if (data?.error) {
                              throw new Error(data.error);
                            }
                            if (!data?.success) {
                              throw new Error('Failed to verify code');
                            }
                            
                            setPasswordVerified(true);
                            toast.success('Code verified! Enter your new password.');
                          } catch (error: any) {
                            console.error('Verify error:', error);
                            let message = error.message || 'Invalid or expired code';
                            if (message.includes('non-2xx') || message.includes('FunctionsHttpError')) {
                              message = 'Incorrect code. Please check and try again.';
                            }
                            setPasswordError(message);
                          } finally {
                            setVerifyingCode(false);
                          }
                        }}
                        disabled={verifyingCode || resetCode.length !== 6}
                      >
                        {verifyingCode ? 'Verifying...' : 'Verify'}
                      </Button>
                    </DialogFooter>
                  </div>
                )}
              </>
            ) : (
              /* Step 2: Enter New Password (only shown after verification) */
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="p-3 border rounded-lg bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Identity verified! Enter your new password.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setPasswordError(''); }}
                    placeholder="Enter new password"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(''); }}
                    placeholder="Confirm new password"
                  />
                </div>
                {passwordError && (
                  <p className="text-sm text-destructive">{passwordError}</p>
                )}
                <div className="text-xs text-muted-foreground">
                  <p className="font-medium mb-1">Password must include:</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li>At least 8 characters</li>
                    <li>Uppercase and lowercase letters</li>
                    <li>At least one number</li>
                    <li>At least one special character (!@#$%^&*)</li>
                  </ul>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={changingPassword}>
                    {changingPassword ? 'Updating...' : 'Update Password'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Account Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-500">
                <AlertTriangle className="w-5 h-5" />
                Delete Your Account
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone. All your data will be permanently removed.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleDeleteAccount} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="deletePassword">Enter your password to confirm</Label>
                <Input
                  id="deletePassword"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => { setDeletePassword(e.target.value); setDeleteError(''); }}
                  placeholder="Enter your password"
                />
              </div>
              {deleteError && (
                <p className="text-sm text-red-500">{deleteError}</p>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="destructive" disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete My Account'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MyProfile;
